import { useState } from "react";
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SupportOutlinedIcon from "@mui/icons-material/SupportOutlined";
import { Link, useLocation } from "react-router-dom";
import NgwaahiaIcon from "./NgwaahiaIcon";
import CartButton from "./CartButton";
import AppRedirectModal from "./AppRedirectModal";
import {
  frontendRoutes,
  LANDING_STAY_QUERY_PARAM,
} from "../utility/frontendRoutes";
import useAuthentication from "../hooks/useAuthentication";
import { useQuery } from "@tanstack/react-query";
import { getUserShops } from "../utility/httpActions/shopActions";
import { APP_SCHEME } from "../utility/constants";

const MODAL_CONFIGS = {
  signUp: {
    title: "Sign up in the app",
    body: "Creating an account, managing your profile, and placing orders all happen in the Ngwaahia app.",
  },
  setupShop: {
    title: "Open your shop in the app",
    body: "Shop setup, product listings, and order management are all handled in the Ngwaahia app.",
    deepLink: `${APP_SCHEME}://setup-shop`,
  },
};

const NavBar = () => {
  const { isLoggedIn, currentUser } = useAuthentication();
  const [modal, setModal] = useState(null);
  const location = useLocation();
  const { pathname } = location;
  // From the feed the logo goes to the landing page, from anywhere else it goes
  // to the feed. The stay param stops the landing page bouncing logged-in users
  // straight back to the feed.
  const logoTo =
    pathname === frontendRoutes.HOME
      ? `${frontendRoutes.LANDING}?${LANDING_STAY_QUERY_PARAM}=true`
      : frontendRoutes.HOME;

  const { data } = useQuery({
    queryKey: ["shops", { page: 1, limit: 10 }],
    queryFn: () => getUserShops({ page: 1, limit: 10 }),
    enabled: isLoggedIn,
  });
  const hasShop = (data?.userShops?.length || 0) > 0;

  return (
    <>
      <AppBar
        elevation={0}
        sx={{
          bgcolor: "white",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Toolbar
          sx={{
            height: 60,
            minHeight: "60px !important",
            px: { xs: 2, md: 4 },
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          {/* Logo */}
          <Box
            component={Link}
            to={logoTo}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              textDecoration: "none",
              mr: "auto",
            }}
          >
            <NgwaahiaIcon size={32} color="#046287" />
            <Typography
              fontWeight={700}
              fontSize={18}
              color="#046287"
              sx={{ display: { xs: "none", sm: "block" } }}
            >
              Ngwaahia
            </Typography>
          </Box>

          <IconButton
            component={Link}
            to={frontendRoutes.SEARCH}
            aria-label="Search"
            sx={{
              color: "#046287",
              display: { xs: "inline-flex", sm: "none" },
            }}
          >
            <SearchIcon />
          </IconButton>
          <Button
            component={Link}
            to={frontendRoutes.SEARCH}
            size="small"
            startIcon={<SearchIcon />}
            sx={{
              color: "#046287",
              fontWeight: 600,
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            Search
          </Button>

          <IconButton
            component={Link}
            to={frontendRoutes.HELP}
            aria-label="Help"
            sx={{
              color: "#046287",
              display: { xs: "inline-flex", sm: "none" },
            }}
          >
            <SupportOutlinedIcon />
          </IconButton>
          <Button
            component={Link}
            to={frontendRoutes.HELP}
            size="small"
            startIcon={<SupportOutlinedIcon />}
            sx={{
              color: "#046287",
              fontWeight: 600,
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            Help
          </Button>

          {/* Right-side actions */}
          {isLoggedIn ? (
            <>
              {!hasShop && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<NgwaahiaIcon size={16} color="#046287" />}
                  onClick={() => setModal("setupShop")}
                  sx={{
                    borderColor: "#046287",
                    color: "#046287",
                    borderRadius: 2,
                    display: { xs: "none", sm: "flex" },
                  }}
                >
                  Set up shop
                </Button>
              )}
              <Button
                component={Link}
                to={frontendRoutes.USER_PROFILE}
                sx={{
                  color: "#046287",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                <Box
                  component="span"
                  sx={{ display: { xs: "none", sm: "inline" } }}
                >
                  Hello,&nbsp;
                </Box>
                {currentUser?.firstName}
              </Button>
            </>
          ) : (
            <>
              <Button
                component={Link}
                to={frontendRoutes.LOGIN}
                // Return to this page after logging in (see GuestRoute).
                state={
                  pathname === frontendRoutes.LOGIN
                    ? location.state
                    : { from: location }
                }
                sx={{ color: "#046287", fontWeight: 600 }}
              >
                Log in
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setModal("signUp")}
                sx={{
                  bgcolor: "#046287",
                  "&:hover": { bgcolor: "#035070" },
                  borderRadius: 2,
                  fontWeight: 700,
                  px: 2,
                }}
              >
                Sign up
              </Button>
            </>
          )}

          <CartButton />
        </Toolbar>
      </AppBar>

      {modal && (
        <AppRedirectModal
          open
          onClose={() => setModal(null)}
          {...MODAL_CONFIGS[modal]}
        />
      )}
    </>
  );
};

export default NavBar;
